import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import { IEventSessionType } from "../interfaces/IEventSessionType";
import { EventSessionTypeService } from "../services/eventTypeService";
import { RootState } from "./store";

// Selectors
export const selectEventSessionTypesLoading = (state: RootState) => state.eventSessionTypes.loading;
export const selectEventSessionTypes = (state: RootState) => state.eventSessionTypes.eventSessionTypes;

// Async actions
export const fetchEventSessionTypes = createAsyncThunk<
    IEventSessionType[],
    AxiosInstance,
    { state: RootState }
>(
    "eventSessionTypes/fetch",
    async (apiClient, { getState }) => {
        const { eventSessionTypes, lastFetched } = getState().eventSessionTypes;
        if (eventSessionTypes.length > 0 && Date.now() - lastFetched < 5 * 60 * 1000) {
            // Return cached data
            return eventSessionTypes;
        }
        const eventSessionTypeService = new EventSessionTypeService(apiClient);
        return await eventSessionTypeService.getAll<IEventSessionType[]>();
    },
    {
        condition: (apiClient, { getState }) => {
            const { eventSessionTypes, lastFetched } = getState().eventSessionTypes;
            if (eventSessionTypes.length > 0 && Date.now() - lastFetched < 5 * 60 * 1000) {
                return false;
            }
            return true;
        }
    }
);

export const addEventSessionType = createAsyncThunk<IEventSessionType, { eventSessionType: IEventSessionType; apiClient: AxiosInstance }>(
    "eventSessionTypes/add",
    async ({ eventSessionType, apiClient }) => {
        const eventSessionTypeService = new EventSessionTypeService(apiClient);
        return await eventSessionTypeService.create(eventSessionType);
    }
);

export const updateEventSessionType = createAsyncThunk<IEventSessionType, { eventSessionTypeId: string; updatedEventSessionType: IEventSessionType; apiClient: AxiosInstance }>(
    "eventSessionTypes/update",
    async ({ eventSessionTypeId, updatedEventSessionType, apiClient }) => {
        const eventSessionTypeService = new EventSessionTypeService(apiClient);
        return await eventSessionTypeService.update(eventSessionTypeId, updatedEventSessionType);
    }
);

export const deleteEventSessionType = createAsyncThunk<string, { eventSessionTypeId: string; apiClient: AxiosInstance }>(
    "eventSessionTypes/delete",
    async ({ eventSessionTypeId, apiClient }) => {
        const eventSessionTypeService = new EventSessionTypeService(apiClient);
        await eventSessionTypeService.delete(eventSessionTypeId);
        return eventSessionTypeId;
    }
);

const eventSessionTypesSlice = createSlice({
    name: "eventSessionTypes",
    initialState: {
        eventSessionTypes: [] as IEventSessionType[],
        loading: false,
        lastFetched: 0
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchEventSessionTypes.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchEventSessionTypes.fulfilled, (state, action) => {
                state.loading = false;
                state.eventSessionTypes = action.payload;
                state.lastFetched = Date.now();
            })
            .addCase(addEventSessionType.fulfilled, (state, action) => {
                state.eventSessionTypes.push(action.payload);
            })
            .addCase(updateEventSessionType.fulfilled, (state, action) => {
                state.eventSessionTypes = state.eventSessionTypes.map(type =>
                    type.id === action.payload.id ? action.payload : type
                );
            })
            .addCase(deleteEventSessionType.fulfilled, (state, action) => {
                state.eventSessionTypes = state.eventSessionTypes.filter(type => type.id !== action.payload);
            });
    }
});

export default eventSessionTypesSlice.reducer;
